import urls from '../api/urls';
import config from '../libs/webIMConfig';
import GroupDetail from '../models/groupDetails';
import { IGroupMedia } from '../types/clubProfile';
import { addParamsToQueryString } from './utils';

interface IVideoUploadFunction {
  onFileUploadProgress?: (p: { loaded: number; total: number }) => void;
  onFileUploadComplete?: (ts: IGroupMedia) => void;
  onFileUploadError?: (params: any) => void;
  onFileUploadCanceled?: () => void;
  file: any;
  id: string | number;
  compress: boolean;
  ext?: IEXT;
  mergeURL: string;
  progressUrl: string;
  mergedMetaData: {};
}

interface IEXT {
  userVideoUploadMaxRetries?: number;
  userVideoUploadPollInterval?: number;
}

const splitChunk = (params: { fileSize: number; compress: boolean; file: any }) => {
  const { fileSize, compress, file } = params;
  let chunks = [];
  const chunkSize = (fileSize < 5 ? 1 : 5) * 1024 * 1024; // 5 MB chunk size
  let start = 0;
  let end = chunkSize;
  if (!compress) {
    chunks = file.fileData;
  } else {
    while (start < fileSize) {
      chunks.push(file.slice(start, end));
      start = end;
      end = start + chunkSize;
    }
  }
  return chunks;
};
const checkUploadFileStatus = (params: IVideoUploadFunction) => {
  const { ext, id, onFileUploadComplete, onFileUploadError, progressUrl, mergedMetaData } = params;
  if (progressUrl) {
    const _mergeProgressUrl = addParamsToQueryString(`${urls.BASE_RHPURL}/${progressUrl}`, mergedMetaData);
    let timeRetry = 0;
    const maxTimeRetry = ext?.userVideoUploadMaxRetries || 20;
    const debounceRetryTime = ext?.userVideoUploadPollInterval || 3000;
    const interval = setInterval(() => {
      if (timeRetry <= maxTimeRetry && config.videoUploadController?.[id]) {
        let controller = new AbortController();
        const signal = controller.signal;
        config.videoUploadController[id].push?.(controller);
        fetch(_mergeProgressUrl, {
          headers: {
            Accept: `application/json`,
            Authorization: 'Bearer ' + config.rhpToken,
            'Content-Type': 'application/json',
          },
          method: 'GET',
          signal,
        })
          .then(res => {
            return res.json();
          })
          .then(response => {
            if (response.Status === 'InProgress') {
              timeRetry += 1;
            } else if (response.Status === 'Complete') {
              clearInterval(interval);
              if (config.videoUploadController[id]) {
                delete config.videoUploadController[id];
              }
              onFileUploadComplete?.(GroupDetail.mappingGroupMedia(response.Response.Video));
            } else {
              clearInterval(interval);
              onFileUploadError?.({
                message: response.ResponseStatus.ErrorCode || 'Upload progress check failed',
                id: id,
              });
            }
          })
          .catch(e => {
            clearInterval(interval);
            onFileUploadError?.({
              message: e || 'Upload progress check failed',
              id: id,
            });
          });
      } else {
        clearInterval(interval);
        onFileUploadError?.({
          message: config.videoUploadController?.[id] ? 'Request timeout' : 'The upload of your video has been cancelled.',
          id: id,
        });
      }
    }, debounceRetryTime);
  }
};
const mergeChunks = (params: {
  options: IVideoUploadFunction;
  fileNameWithoutExtension: any;
  timeStamp: any;
  fileExtension: any;
  chunksLength: number;
}) => {
  const { fileNameWithoutExtension, timeStamp, fileExtension, chunksLength } = params;
  const { id, compress, mergeURL, onFileUploadError, mergedMetaData } = params.options;
  const _mergeURL = `${urls.BASE_RHPURL}/${mergeURL}`;

  let controller = new AbortController();
  const signal = controller.signal;
  config.videoUploadController?.[id]?.push?.(controller);
  const body = {
    FileName: `${fileNameWithoutExtension}${timeStamp}.${fileExtension}`,
    NumberOfChunks: chunksLength,
    Compress: compress,
    Is64EncodedString: !compress,
    ...mergedMetaData,
  };
  fetch(_mergeURL, {
    body: JSON.stringify(body),
    headers: {
      Accept: `application/json`,
      Authorization: 'Bearer ' + config.rhpToken,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    signal,
  })
    .then(async response => {
      if (response.status === 200) {
        if (params.options.progressUrl) {
          checkUploadFileStatus(params.options);
        } else {
          const data = (await response.json()) as any;
          params.options.onFileUploadComplete?.(data);
        }
      } else {
        throw 'Video merge error';
      }
    })
    .catch(e => {
      onFileUploadError?.({
        message: e,
        id: id,
      });
    });
};
export const videoUpload = (options: any) => {
  try {
    config.videoUploadController[options.id] = [];
    const timeStamp = options.file?.timeStamp || new Date().getTime();
    const file = options.file || {};
    const fileSize = file.size || undefined;
    if (file.size <= 0) {
      return options.onFileUploadError?.({
        message: 'Unvalid video file',
        id: options.id,
      });
    }
    const chunks = splitChunk({ fileSize, file, compress: options.compress });
    const nameParts = file.name.split('.');
    const fileExtension = nameParts.pop(); // get extension (.mp4)
    const fileNameWithoutExtension = nameParts.join('.');
    let numFileUploadCompleted = 0;
    for (const chunk of chunks) {
      let controller = new AbortController();
      const signal = controller.signal;
      const index = chunks.indexOf(chunk);
      let formData = new FormData();
      let bodyData = {};
      let uploadUrl = `${urls.BASE_RHPURL}/${urls.VIDEO_UPLOAD_CHUNKS}`;
      config.videoUploadController?.[options.id]?.push?.(controller);
      const chunkName = `${fileNameWithoutExtension}${timeStamp}.${fileExtension}.part_${index + 1}.${chunks.length}`;
      let headers: HeadersInit = {
        Accept: `application/json`,
        Authorization: 'Bearer ' + config.rhpToken,
      };
      if (!options.compress) {
        uploadUrl = `${urls.BASE_RHPURL}/${urls.VIDEO_UPLOAD_CHUNKS_STRING}`;
        bodyData = {
          FileName: `${fileNameWithoutExtension}${timeStamp}.${fileExtension}`,
          FileContent: chunk,
          ChunkNumber: index + 1,
          NumberOfChunks: chunks.length,
        };
        headers = {
          ...headers,
          'Content-Type': 'application/json',
        };
      } else {
        formData.append('chunk', chunk, chunkName);
      }
      fetch(uploadUrl, {
        body: options.compress ? formData : JSON.stringify(bodyData),
        headers,
        method: 'POST',
        signal,
      })
        .then(response => {
          if (response.status === 200) {
            numFileUploadCompleted = numFileUploadCompleted + 1;
            if (numFileUploadCompleted === chunks.length) {
              options.onFileUploadProgress?.({
                loaded: numFileUploadCompleted,
                total: chunks.length,
              });
              setTimeout(() => {
                mergeChunks({
                  options: options,
                  fileNameWithoutExtension: fileNameWithoutExtension,
                  timeStamp: timeStamp,
                  fileExtension: fileExtension,
                  chunksLength: chunks.length,
                });
              }, 1000);
            } else {
              options.onFileUploadProgress?.({
                loaded: numFileUploadCompleted,
                total: chunks.length,
              });
            }
          } else {
            return options.onFileUploadError?.({
              message: 'Video upload error',
              id: options.id,
            });
          }
        })
        .catch(e => {
          return options.onFileUploadError?.({
            message: e || 'Video upload error',
            id: options.id,
          });
        });
    }
  } catch (error) {
    return options.onFileUploadError?.({
      message: error,
      id: options.id,
    });
  }
};
